import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  const refresh = useCallback(async () => {
    if (!user) { setCart([]); setWishlist([]); return; }
    try {
      const [c, w] = await Promise.all([api.get("/cart"), api.get("/wishlist")]);
      setCart(c.data); setWishlist(w.data);
    } catch {}
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const requireAuth = () => {
    if (!user) { toast.error("Please sign in to continue"); return false; }
    return true;
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!requireAuth()) return;
    const { data } = await api.post("/cart", { product_id: productId, quantity });
    setCart(data);
    toast.success("Added to bag");
  };

  const updateCart = async (productId, quantity) => {
    const { data } = await api.put(`/cart/${productId}`, { product_id: productId, quantity });
    setCart(data);
  };

  const removeFromCart = async (productId) => {
    const { data } = await api.delete(`/cart/${productId}`);
    setCart(data);
  };

  const toggleWishlist = async (productId) => {
    if (!requireAuth()) return;
    const isIn = wishlist.some((p) => p.id === productId);
    if (isIn) {
      await api.delete(`/wishlist/${productId}`);
      setWishlist(wishlist.filter((p) => p.id !== productId));
      toast.success("Removed from wishlist");
    } else {
      await api.post("/wishlist", { product_id: productId });
      toast.success("Added to wishlist");
      refresh();
    }
  };

  const inWishlist = (productId) => wishlist.some((p) => p.id === productId);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + i.product.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart, wishlist, cartCount, cartTotal,
      addToCart, updateCart, removeFromCart, toggleWishlist, inWishlist, refresh,
    }}>
      {children}
    </CartContext.Provider>
  );
};
